import { Injectable } from "@angular/core";
import { Order, OrderFile } from "../models/order.model";
import {
  ChatContact,
  ChatMsg,
  ChatSeenBy,
  ChatParticipantActivity,
  UserNotification,
  NotificationTypeEnum,
  ChatTask,
} from "../models/chat.model";
import { Observable, of } from "rxjs";
import { ServiceOptions } from "../models/service-options";
import { AppState } from "../redux/store";
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
} from "@angular/common/http";
import { AuthService } from "./auth.service";
import { DatePipe } from "@angular/common";
import { map, catchError, take } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import { UiUser } from "../models/ui-user.model";
import { DataElement, ServiceCallInfo } from "../models/common";
import { Store } from "@ngrx/store";
import mime from "mime";

@Injectable({
  providedIn: "root",
})
export class ChatService {
  constructor(
    private readonly store: Store,
    private http: HttpClient,
    private authService: AuthService,
    private datePipe: DatePipe,
    private translateService: TranslateService
  ) { }

  getChatRecipientTypes(order: Order): Observable<ChatContact[]> {
    let response = new Observable<ChatContact[]>();
    let serviceName = "get_chat_recipient_types";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = order.orderId;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractChatRecipientTypes(data, info)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractChatRecipientTypes(
    res: any,
    info: ServiceCallInfo
  ): ChatContact[] {
    this.authService.extractRequestStatusNew(info, res);
    let result: ChatContact[] = [];
    let data = res.reply.requestResult.data;

    if (data && data.chat) {
      let tag = data.chat;
      let arr = new Array().concat(tag);
      arr.forEach((i) => {
        let c = new ChatContact(this.translateService);
        c.contactId = i.toType;
        c.name = i.toTypeDescr;
        // c.lastSeen = this.authService.fromXMLDate(i.lastSeen);
        c.curDbDate = this.authService.fromXMLDate(i.curDbDate);
        result.push(c);
      });
    }

    return result;
  }

  getOrderChatMessages(
    order: Order,
    contact: ChatContact
  ): Observable<ChatMsg[]> {
    let response = new Observable<ChatMsg[]>();
    let serviceName = "get_order_chat_msgs";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = order.orderId;
    params["chatWithType"] = contact.contactId;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractOrderChatMessages(data, info)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractOrderChatMessages(res: any, info: ServiceCallInfo): ChatMsg[] {
    this.authService.extractRequestStatusNew(info, res);
    let result: ChatMsg[] = [];
    let data = res.reply.requestResult.data;

    if (
      data &&
      data.chatArr &&
      data.chatArr.chat &&
      data.chatArr.chat.msgArr &&
      data.chatArr.chat.msgArr.msg
    ) {
      let tag = data.chatArr.chat.msgArr.msg;
      let arr = new Array().concat(tag);
      arr.forEach((i) => {
        let c = new ChatMsg();
        c.msgId = i.msgId;
        c.fromType = i.fromType;
        c.fromUserId = i.fromUserId;
        c.fromUserDescr = i.fromUserDescr;
        c.fromUserType = i.fromUserType;
        c.timestamp = this.authService.fromXMLDate(i.timestamp);
        c.msgText = i.msgText;
        c.modelType = i.modelType;
        c.contentS3Key = i.contentS3Key;
        c.contentUrl = i.contentUrl;
        c.contentType = i.contentType;
        c.audioDur = i.audioDur;
        if (c.contentType == "AUDIO") c.audioSrc = c.contentUrl!;
        // c.audioSrc = [
        //   {
        //     src: c.contentUrl!,
        //     type: mime.getType(c.contentUrl!),
        //   },
        // ];

        if (i.fileId) {
          let file = new OrderFile();
          file.fileId = i.fileId;
          file.fileName = i.fileName;
          if (file.fileName) {
            file.fileName = file.fileName.split("\\").pop().split("/").pop();
          }
          file.fileType = i.fileType.toLowerCase();
          file.uploadDate = i.fileUploadTimestamp;
          file.uploadDateFormatted = this.datePipe.transform(
            file.uploadDate,
            "dd/MM/yy"
          );
          file.url = i.fileUrl;
          file.modelType = c.modelType;
          file.mimeType = (mime as any).getType(file.url);
          if (file.mimeType?.split("/")[0] == "video") {
            file.isVideo = true;
            file.videoSrc = file.url;
            // file.videoSrc = [
            //   {
            //     src: file.url,
            //     type: file.mimeType,
            //   },
            // ];
          }

          c.file = file;
        }

        c.isMine = c.fromType == this.authService.currentUser.org;
        if (
          c.fromType == this.authService.currentUser.org &&
          !(
            this.authService.currentUser.org == "LAB" &&
            c.fromUserType == "PLANT_AGENT"
          )
        ) {
          c.readByMe = true;
        }
        c.seenBy = [];
        if (i.seenBy) {
          let seenArr = new Array().concat(i.seenBy);
          seenArr.forEach((i) => {
            let seen = new ChatSeenBy();
            seen.timestamp = this.authService.fromXMLDate(i.timestamp);
            seen.userId = i.userId;
            seen.userType = i.userType;
            seen.userDescr = i.userDescr;
            seen.userRoleDescr = i.userRoleDescr;
            seen.userOrg = i.userOrg;
            if (seen.userId == this.authService.currentUser.userId) {
              c.readByMe = true;
            }
            c.seenBy.push(seen);
          });
        }

        c.taskId = i.taskId;

        result.push(c);
      });
    }

    return result;
  }

  sendChatMsg(
    order: Order,
    contact: ChatContact,
    msg: ChatMsg
  ): Observable<boolean> {
    let response = new Observable<boolean>();
    let serviceName = "send_chat_msg";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = order.orderId;
    params["orderIdMmd"] = order.orderIdMmd;
    params["recipientType"] = contact.contactId;
    params["msgText"] = msg.msgText;
    if (msg.fileId) params["fileId"] = msg.fileId;
    params.urldecodeSw = 1;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.authService.extractRequestStatusNew(info, data)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  markChatMsgRead(order: Order, msgList: ChatMsg[]): Observable<boolean> {
    let response = new Observable<boolean>();
    let serviceName = "mark_chat_msg_read";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = order.orderId;
    params["orderIdMmd"] = order.orderIdMmd;
    params["msgIdList"] = msgList.map((m) => m.msgId).join(",");

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.authService.extractRequestStatusNew(info, data)),
        catchError((error) => {
          console.log(error);
          return of(true);
          // return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  getUserNotifications(): Observable<UserNotification[]> {
    let response = new Observable<UserNotification[]>();
    let serviceName = "get_user_notifications";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    info.alertWhenOffline = false;
    info.online = navigator.onLine;
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractUserNotifications(data, info)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractUserNotifications(
    res: any,
    info: ServiceCallInfo
  ): UserNotification[] {
    this.authService.extractRequestStatusNew(info, res);
    let result: UserNotification[] = [];
    let data = res.reply.requestResult.data;

    if (data && data.n) {
      let tag = data.n;

      if (tag) {
        let arr = new Array().concat(tag);
        arr.forEach((i) => {
          let nf = new UserNotification();
          nf.id = i.nId;
          nf.orderId = i.orderId;
          nf.orderIdMmd = i.orderIdMmd;
          nf.clinicId = i.clinicId;
          nf.readSw = this.authService.fromXMLBoolean(i.readSw);
          nf.requireReadSw = this.authService.fromXMLBoolean(i.requireReadSw);

          nf.text = i.text;
          nf.timestamp = this.authService.fromXMLDate(i.timestamp);
          nf.type = i.nType;

          if (nf.type == NotificationTypeEnum.ORDER_DUE_DATE_UPD) {
            nf.importantSw = true;
          }
          result.push(nf);
        });
      }
    }
    return result;
  }

  markUserNotificationRead(nf: UserNotification[]): Observable<boolean> {
    let response = new Observable<boolean>();
    let serviceName = "mark_notification_read_sw";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["nIdList"] = nf.map((n) => n.id).join(",");

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.authService.extractRequestStatusNew(info, data)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  deleteChatMsg(msg: ChatMsg, order: Order): Observable<boolean> {
    let response = new Observable<boolean>();
    let serviceName = "delete_chat_msg";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = order.orderId;
    params["orderIdMmd"] = order.orderIdMmd;
    params["msgId"] = msg.msgId;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.authService.extractRequestStatusNew(info, data)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  updChatStat(clinicId: number): Observable<boolean> {
    let response = new Observable<boolean>();
    let serviceName = "updChatStat.do";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["cliNum"] = clinicId;

    let url = this.authService.getRequestUrl(serviceName, true);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body, true);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.authService.extractRequestStatusNew(info, data)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  public getChatStat(clinicId: number): Observable<number> {
    let response = new Observable<number>();

    response = this.http
      .get(
        this.authService.chatStatBucket +
        clinicId +
        ".json" +
        "?dummyVar=" +
        new Date().getTime(),
        {
          headers: new HttpHeaders({
            "Content-Type": "application/json",
          }),
          withCredentials: false,
          responseType: "json",
        }
      )
      .pipe(
        take(1),
        map((data) => {
          let dataRep = data as any;
          let date = dataRep.modifyDate as number;
          return date;
        }),
        catchError((error: HttpErrorResponse) => {
          /* if (error.status == 403) {
            return of(null);
          }
          return this.authService.handleError(error, "getChatStat");*/
          return of(null);
        })
      );

    return response;
  }

  getChatTasks(order: Order): Observable<ChatTask[]> {
    let response = new Observable<ChatTask[]>();
    let serviceName = "get_order_chat_tasks";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = order.orderId;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractOrderChatTasks(data, info)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractOrderChatTasks(res: any, info: ServiceCallInfo): ChatTask[] {
    this.authService.extractRequestStatusNew(info, res);
    let result: ChatTask[] = [];
    let data = res.reply.requestResult.data;

    if (data && data.task) {
      let tag = data.task;
      let arr = new Array().concat(tag);
      arr.forEach((i) => {
        let task = new ChatTask();
        task.taskId = i.taskId;
        task.msgId = i.msgId;
        task.msgTxt = i.msgTxt;
        task.msgTimestamp = i.msgTimestamp;
        task.msgFromUserDescr = i.msgFromUserDescr;
        task.taskTitle = i.title;
        let owner = new UiUser();
        owner.userId = i.ownerUserId;
        owner.userDescr = i.ownerUserDescr;
        task.owner = owner;
        task.notes = i.notes;
        task.dueDate = this.authService.fromXMLDate(i.dueDate);
        task.status = new DataElement(i.status, i.statusDescr);
        task.createUserDescr = i.createUserDescr;
        task.createTimestamp = i.createTimestamp;
        task.lastUpdUserDescr = i.lastUpdUserDescr;
        task.lastUpdTimestamp = i.lastUpdTimestamp;
        result.push(task);
      });
    }

    return result;
  }

  createUpdateChatTask(order: Order, task: ChatTask): Observable<boolean> {
    let response = new Observable<boolean>();

    let serviceName = task.taskId ? "update_chat_task" : "create_chat_task";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = order.orderId;
    params["orderIdMmd"] = order.orderIdMmd;
    params["msgId"] = task.msgId;
    params["taskId"] = task.taskId;
    if (task.owner) params["ownerUserId"] = task.owner.userId;
    if (task.dueDate) params["dueDate"] = task.dueDate.to_DD_MM_YYYY();
    if (task.status) params["status"] = task.status.id;
    params["title"] = task.taskTitle;
    params["notes"] = task.notes;
    params.urldecodeSw = 1;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.authService.extractRequestStatusNew(info, data)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  getChatParticipantsActivity(
    order: Order,
    chatType: string
  ): Observable<ChatParticipantActivity[]> {
    let response = new Observable<ChatParticipantActivity[]>();
    let serviceName = "get_chat_participants_activity";
    let params = new ServiceOptions();
    params.serviceName = serviceName;

    params["orderId"] = order.orderId;
    params["chatType"] = chatType;

    let url = this.authService.getRequestUrl(serviceName);
    let body = this.authService.getRequestBody(params);
    let info = new ServiceCallInfo(serviceName, url, body);
    response = this.http
      .post(url, body, {
        headers: this.authService.getRequestHeaders(),
        withCredentials: false,
        responseType: "json",
      })
      .pipe(
        map((data) => this.extractChatParticipantsActivity(data, info)),
        catchError((error) => {
          return this.authService.handleErrorNew(error, info);
        })
      );

    return response;
  }

  private extractChatParticipantsActivity(
    res: any,
    info: ServiceCallInfo
  ): ChatParticipantActivity[] {
    this.authService.extractRequestStatusNew(info, res);
    let result: ChatParticipantActivity[] = [];
    let data = res.reply.requestResult.data;

    if (data && data.participants) {
      let participants = new Array().concat(data.participants);
      participants.forEach((i) => {
        let p = new ChatParticipantActivity();
        p.userId = i.userId;
        p.userName = i.userName;
        p.lastActivityDate = this.authService.fromXMLDate(i.lastActivityDate);
        result.push(p);
      });
    }

    return result;
  }
}
